import './NotificationPreferences.scss';

import { Alert, Checkbox, Form, FormProps, Input, Modal, Pagination, Segmented, Select, Switch, Tabs, Tooltip } from 'antd';
import React, { useState, useEffect, useCallback, memo, useMemo } from 'react'
import { Badge, Breadcrumb, Card, Collapse, Container, Button, Col, Row } from 'react-bootstrap';
import { Notifications } from '../../api/Notifications';
import { useToasts } from '../../contexts/toasts';
import { TailSpin } from 'react-loader-spinner';
import { Applications } from '../../api/Applications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { accessLevelOptions, formatDate, notificationTypesOptions } from '../../utils';
import { Link } from 'react-router-dom';
import IconButton from '../../components/common/IconButton';
import TriggerItem from '../../components/list-items/TriggerItem';
import { cloneDeep } from 'lodash';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { NotificationPreferences, NotificationTrigger } from '../../models/Notification';
import { BdaApplication } from '../../models/BdaApplication';
import { AccessLevel, PagedResults } from '../../models/generics';
import TriggerEditForm from '../../components/forms/TriggerEditForm';
import AppSearch from '../../components/common/AppSearch';
import { useAuth } from '../../contexts/auth';
import UserItem from '../../components/common/UserItem';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../../components/common/AppLoader';

const layout: FormProps = {
  colon: false,
  labelCol: {
    xs: 24,
    sm: 12,
    md: 6
  },
  wrapperCol: {
    xs: 24,
    sm: 12,
    md: 18
  },
  labelAlign: 'left',
};

const tabs = [
  {
    key: 'web',
    label: (
      <>
        <FontAwesomeIcon icon={solid('globe')} />
        <span className="ms-2">Web Preferences</span>
      </>
    ),
  },
  {
    key: 'feed',
    label: (
      <>
        <FontAwesomeIcon icon={solid('rss')} />
        <span className="ms-2">Feed Preferences</span>
      </>
    ),
  },
  {
    key: 'triggers',
    label: (
      <>
        <FontAwesomeIcon icon={solid('bolt')} />
        <span className="ms-2">Triggers</span>
      </>
    ),
  },
];

export default function NotificationPreferencesPage() {
  const [form] = Form.useForm();

  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState<BdaApplication[]>();

  const [userPreferences, setUserPreferences] = useState<NotificationPreferences>();
  const [showApplicationsPreferences, setShowApplicationsPreferences] = useState(true);
  const [showEmailPreferences, setShowEmailPreferences] = useState(true);
  const [error, setError] = useState(false);
  const [showSubmit, setShowSubmit] = useState(true);
  const [triggers, setTriggers] = useState<NotificationTrigger[]>();
  const [editing, setEditing] = useState<number>();
  const [editingTrigger, setEditingTrigger] = useState<NotificationTrigger>();
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [trigger, setTrigger] = useState<NotificationTrigger>();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  const initTriggers = useCallback(() => {
    Notifications.getTriggers()
      .then(
        ({ data }) => {
          const newTriggers = data.map(trigger => {
            const newTrigger = { ...trigger };
            if (trigger.headers) newTrigger.headers = Object.entries(trigger.headers as unknown as Record<string, string>).map(([key, value]) => ({ key, value }));
            if (trigger.queryParams) newTrigger.queryParams = Object.entries(trigger.queryParams as unknown as Record<string, string>).map(([key, value]) => ({ key, value }));
            return newTrigger;
          });
          setTriggers(newTriggers);
        }
      )
  }, []);

  const initApplications = useCallback(() => {
    if (!userPreferences!.webPreferences.applications.specificApplications || userPreferences!.webPreferences.applications.specificApplications.length === 0) {
      setSelectedApplications([]);
      return;
    }

    const params = new URLSearchParams();
    params.set('ids', userPreferences!.webPreferences.applications.specificApplications.join(','));

    Applications.getApplications(params)
      .then(
        ({ data }) => setSelectedApplications((data as Array<BdaApplication>)),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('preferences-error', message, `Preferences Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [userPreferences, showToast]);

  useEffect(() => {
    if (userPreferences && !triggers) initTriggers();
  }, [userPreferences, triggers, initTriggers]);

  useEffect(() => {
    if (userPreferences) {
      if (userPreferences.webPreferences.applications.monitoringMode === "CUSTOM") initApplications();
      else setSelectedApplications([]);
    }
  }, [userPreferences, initApplications]);

  useEffect(() => {
    form.setFieldsValue(userPreferences);
  }, [form, userPreferences]);

  const getPreferences = useCallback(() => {
    Notifications.getNotificationPreferences()
      .then(
        ({ data }) => setUserPreferences(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('preferences-error', message, `Preferences Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(() => {
    getPreferences();
  }, [getPreferences]);

  const reset = useCallback(() => {
    setUserPreferences(undefined);
    form.setFieldsValue(null);
    getPreferences();
  }, [getPreferences, form]);

  const handleTriggerModalClose = useCallback(() => {
    setEditingTrigger(new NotificationTrigger());
    setEditing(undefined);
    setShowTriggerModal(false);
  }, []);

  const handleApplicationsModalClose = useCallback(() => setShowApplicationsModal(false), []);
  const handleDeleteModalClose = useCallback(() => setShowDeleteModal(false), []);
  const handleAddTrigger = useCallback(() => setShowTriggerModal(true), []);
  const openApplicationsModal = useCallback(() => setShowApplicationsModal(true), []);

  const togglePreferences = useCallback((group: 'applications' | 'email') => () => {
    if (group === "applications") setShowApplicationsPreferences(show => !show);
    else if (group === "email") setShowEmailPreferences(show => !show);
  }, []);

  const handleTabChange = useCallback((key: string) => {
    if (key === 'triggers') setShowSubmit(false);
    else setShowSubmit(true);
  }, []);

  const handleFormSubmit = useCallback((preferences: NotificationPreferences) => {
    showLoader();

    const body = { ...userPreferences, ...preferences };
    if (body.webPreferences.applications.monitoringMode === "CUSTOM") {
      body.webPreferences.applications.specificApplications = selectedApplications?.map(app => app.id!) || [];
    }

    Notifications.saveNotificationPreferences(body)
      .then(
        () => {
          hideLoader();
          showToast('preferences-success', 'Preferences saved successfully', null, 'success');
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('preferences-error', message, `Preferences Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, reset, selectedApplications, showToast, userPreferences]);

  const handleTriggerSubmit = useCallback((trigger: NotificationTrigger) => {
    showLoader();
    const newHeaders = trigger.headers.reduce((acc, curr) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {} as any);
    const newParams = trigger.queryParams.reduce((acc, curr) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {} as any);

    const body = {
      ...trigger,
      headers: newHeaders,
      queryParams: newParams,
    }

    const promise = editing !== undefined
      ? Notifications.updateTrigger({ ...editingTrigger!, ...body })
      : Notifications.createTrigger(body);

    promise.then(
      () => {
        hideLoader();
        showToast(`trigger-${editing !== undefined ? 'update' : 'create'}-success`, `Trigger ${editing !== undefined ? 'updated' : 'created'} successfully`, 'Trigger Success', 'success');
        handleTriggerModalClose();
        setTriggers(undefined);
        initTriggers();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('triggers-error', message, `Triggers Error ${status ? `(${status})` : ''}`, 'danger');
      }
    );
  }, [showLoader, editing, editingTrigger, hideLoader, showToast, handleTriggerModalClose, initTriggers]);

  const handleTriggerEdit = useCallback((index: number) => () => {
    setEditing(index);
    const trigger = cloneDeep(triggers![index]);
    setEditingTrigger(trigger);
    setShowTriggerModal(true);
  }, [triggers]);

  const handleTriggerDelete = useCallback((trigger: NotificationTrigger) => () => {
    setTrigger(trigger);
    setShowDeleteModal(true);
  }, []);

  const deleteTrigger = () => {
    Notifications.deleteTrigger(trigger!.id!)
      .then(
        () => {
          setShowDeleteModal(false);
          showToast(`trigger-delete-success`, "Trigger deleted successfully", 'Trigger Success', 'success');
          setTriggers(undefined);
          initTriggers();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('triggers-error', message, `Triggers Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleApplicationConfirm = useCallback((applications: BdaApplication[]) => {
    setSelectedApplications(applications);
    setShowApplicationsModal(false);
  }, []);

  const removeApplication = useCallback((id: number) => () => {
    setSelectedApplications(apps => apps!.filter(app => app.id !== id));
  }, []);

  const renderFeedPreferences = useCallback(() => {
    return (
      <>
        <IconButton type="button" onClick={togglePreferences("email")} className="mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={showEmailPreferences ? solid('chevron-down') : solid('chevron-right')} fixedWidth />
            <span className="ms-2">Email</span>
          </h6>
        </IconButton>

        <Collapse in={showEmailPreferences}>
          <div>
            <div className="ps-4">
              <Form.Item name={['feedPreferences', 'email', 'enabled']} label="Emails Enabled" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item noStyle dependencies={[['feedPreferences', 'email', 'enabled']]}>
                {({ getFieldValue }) => (
                  <Form.Item
                    name={['feedPreferences', 'email', 'address']}
                    label="Email address"
                    extra="Insert the email address you want to receive the notifications."
                    rules={[{ type: 'email', message: "Email must be a valid email" }]}
                  >
                    <Input placeholder="Email address" disabled={!getFieldValue(['feedPreferences', 'email', 'enabled'])} />
                  </Form.Item>
                )}
              </Form.Item>
            </div>
          </div>
        </Collapse>
      </>
    )
  }, [showEmailPreferences, togglePreferences]);

  const renderWebPreferences = useCallback(() => {
    return (
      <>
        <Form.Item className="mb-3" name={['webPreferences', 'enabled']} label="Web Notifications" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item noStyle dependencies={[['webPreferences', 'enabled']]}>
          {({ getFieldValue }) => !getFieldValue(['webPreferences', 'enabled']) && (
            <Alert
              showIcon
              type="warning"
              className="mb-3"
              message="If you disable Web Notifications, you will no longer receive new notifications and they will not show up in the history."
            />
          )}
        </Form.Item>

        <IconButton type="button" onClick={togglePreferences("applications")} className="mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={showApplicationsPreferences ? solid('chevron-down') : solid('chevron-right')} fixedWidth />
            <span className="ms-2">{applicationLabels.plural}</span>
          </h6>
        </IconButton>

        <Collapse in={showApplicationsPreferences}>
          <div>
            <div className="ps-4">
              <Form.Item name={['webPreferences', 'applications', 'enabled']} label={`${applicationLabels.plural} Notifications`} valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item
                name={['webPreferences', 'applications', 'monitoringMode']}
                label={
                  <>
                    <span className="me-2">Monitoring Mode</span>
                    <Tooltip title={
                      <>
                        Monitoring mode values:
                        <ul className="ps-3 m-0">
                          <li>
                            <b>Default</b> - Get notifications from {applicationLabels.plural} you executed
                          </li>
                          <li>
                            <b>My {applicationLabels.plural}</b> - Get notifications about the {applicationLabels.plural} you created
                          </li>
                          <li>
                            <b>Custom</b> - Get just the notifications from {applicationLabels.plural} you specified
                          </li>
                        </ul>
                      </>
                    }
                    >
                      <FontAwesomeIcon icon={solid('info-circle')} />
                    </Tooltip>
                  </>
                }
              >
                <Segmented
                  options={[
                    { label: "Default", value: "DEFAULT" },
                    { label: `My ${applicationLabels.plural}`, value: "OWNED" },
                    { label: "Custom", value: "CUSTOM" },
                  ]}
                />
              </Form.Item>

              <Form.Item noStyle dependencies={[['webPreferences', 'applications', 'monitoringMode']]}>
                {({ getFieldValue }) => getFieldValue(['webPreferences', 'applications', 'monitoringMode']) === "CUSTOM" && (
                  <Form.Item
                    label={
                      <>
                        <span className="me-2">Specific {applicationLabels.plural}</span>
                        <IconButton type="button" tooltip={`Manage ${applicationLabels.plural}`} className="p-0" onClick={openApplicationsModal}>
                          <FontAwesomeIcon icon={solid('cog')} />
                        </IconButton>
                      </>
                    }
                    help={`Manage the list of specific ${applicationLabels.plural} you want to receive notifications about.`}
                  >
                    {userPreferences?.webPreferences.applications.specificApplications && !selectedApplications && (
                      <div className="px-4">
                        <TailSpin
                          height={50}
                          width={50}
                          color="#677ab1"
                          radius="1"
                        />
                      </div>
                    )}

                    {selectedApplications?.length === 0
                      ? (
                        <Alert
                          type="info"
                          showIcon
                          message={`No ${applicationLabels.plural} selected.`}
                          action={
                            <IconButton className="text-primary p-1" type="button" onClick={openApplicationsModal}>
                              <FontAwesomeIcon icon={solid('plus')} />
                              <b className="ms-2">Add {applicationLabels.plural}</b>
                            </IconButton>
                          }
                        />
                      )
                      : (
                        <div className="d-flex flex-wrap mt-2">
                          {selectedApplications?.map(app => (
                            <Col className="mb-2 pe-2" md={4} key={app.id}>
                              <ApplicationItem application={app} selectable={false} removable onRemove={removeApplication(app.id!)} />
                            </Col>
                          ))}
                        </div>
                      )
                    }
                  </Form.Item>
                )}
              </Form.Item>
            </div>
          </div>
        </Collapse>
      </>
    )
  }, [showApplicationsPreferences, applicationLabels, openApplicationsModal, removeApplication, selectedApplications, togglePreferences, userPreferences]);

  const renderTriggers = useCallback(() => {
    if (!triggers) return <AppLoader className="py-5" justify="center" size={80} />;

    if (triggers.length === 0) return (
      <Alert
        showIcon
        type="info"
        message={
          <>
            <span className="me-auto">No triggers found.</span>
            <IconButton type="button" className="p-1 text-primary fw-500" onClick={handleAddTrigger}>Create one now!</IconButton>
          </>
        }
      />
    )

    return (
      <>
        <Row className="justify-content-end mb-3">
          <Button type="button" variant="primary" size="sm" onClick={handleAddTrigger}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Add Trigger</span>
          </Button>
        </Row>

        {triggers.map((trigger, index) => <TriggerItem key={trigger.id} trigger={trigger} onEdit={handleTriggerEdit(index)} onDelete={handleTriggerDelete(trigger)} />)}
      </>
    )
  }, [triggers, handleAddTrigger, handleTriggerDelete, handleTriggerEdit]);

  const renderTab = useCallback((key: string) => {
    switch (key) {
      case 'web':
      default:
        return renderWebPreferences();

      case 'feed':
        return renderFeedPreferences();

      case 'triggers':
        return renderTriggers();
    }
  }, [renderFeedPreferences, renderWebPreferences, renderTriggers]);

  const renderAsyncContent = useCallback(() => {
    if (error) return <Alert type="error" showIcon message="An error occurred, try again later." />;

    if (!userPreferences) return <AppLoader className="py-5" justify="center" size={100} />;

    return (
      <Card>
        <Form form={form} {...layout} onFinish={handleFormSubmit}>
          <Card.Body className="preferences-wrapper">
            <Form.Item name="notificationTypes" label="Notification Types" help="When empty, all notifications will be received.">
              <Select
                allowClear
                mode="multiple"
                placeholder="Select notification types"
                options={notificationTypesOptions}
              />
            </Form.Item>

            <Tabs
              defaultActiveKey="web"
              items={tabs.map(tab => ({
                ...tab,
                children: renderTab(tab.key),
              }))}
              onChange={handleTabChange}
            />
          </Card.Body>
          {showSubmit && (
            <Card.Footer className="d-flex">
              <Button type="submit" className="ms-auto px-3" variant="success" size="sm">
                <FontAwesomeIcon icon={solid('save')} />
                <span className="ms-2">Save</span>
              </Button>
            </Card.Footer>
          )}
        </Form>
      </Card>
    )
  }, [error, userPreferences, form, handleFormSubmit, renderTab, handleTabChange, showSubmit]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/notifications' }}>Notifications</Breadcrumb.Item>
        <Breadcrumb.Item active>Preferences</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Notification Preferences</h5>
        {userPreferences && <small className="text-muted">Last updated: {formatDate(userPreferences.updated)}</small>}
      </div>

      {renderAsyncContent()}

      <ApplicationsModal
        open={showApplicationsModal}
        selectedApplications={selectedApplications || []}
        onCancel={handleApplicationsModalClose}
        onConfirm={handleApplicationConfirm}
      />

      <Modal
        open={showTriggerModal}
        onCancel={handleTriggerModalClose}
        afterClose={handleTriggerModalClose}
        title={editing ? 'Edit Trigger' : 'Create Trigger'}
        footer={null}
        width={800}
      >
        <TriggerEditForm trigger={editingTrigger} onSubmit={handleTriggerSubmit} onCancel={handleTriggerModalClose} />
      </Modal>

      <Modal
        open={showDeleteModal}
        onCancel={handleDeleteModalClose}
        afterClose={handleDeleteModalClose}
        title="Delete Trigger"
        footer={
          <div className="d-flex justify-content-between mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deleteTrigger}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete trigger <b>{trigger?.name}</b>?</p>

        <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
      </Modal>
    </Container>
  )
}

/* ApplicationsModal.tsx */
type ModalProps = {
  open?: boolean;
  selectedApplications: BdaApplication[];
  onCancel?: () => void;
  onConfirm?: (applications: BdaApplication[]) => void;
}

const ApplicationsModal = memo(({ selectedApplications, open, onCancel, onConfirm }: ModalProps) => {
  const [applications, setApplications] = useState<BdaApplication[]>();
  const [totalApplications, setTotalApplications] = useState<number>();
  const [editingApplications, setEditingApplications] = useState<BdaApplication[]>(selectedApplications);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [searchText, setSearchText] = useState("");
  const [accessLevelFilter, setAccessLevelFilter] = useState<AccessLevel>();
  const [myApplications, setMyApplications] = useState(false);
  const [defaultOnly, setDefaultOnly] = useState(true);
  const [showSelected, setShowSelected] = useState(false);

  const { loggedUser } = useAuth();
  const { showToast } = useToasts();

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  useEffect(() => {
    setEditingApplications(selectedApplications);
  }, [selectedApplications, open]);

  const getApplications = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", `${currentPage}`);
    params.set("size", `${itemsPerPage}`);
    params.set("sort", "id");
    params.set("desc", "true");
    if (searchText) params.append('freeText', searchText);
    if (accessLevelFilter) params.append('accessLevel', accessLevelFilter);
    if (myApplications) params.set("createdBy", loggedUser?.sub || "");
    params.append('defaultVersion', `${defaultOnly}`);

    Applications.getApplications(params)
      .then(
        ({ data }) => {
          setApplications((data as PagedResults<BdaApplication>).content);
          setTotalApplications((data as PagedResults<BdaApplication>).pageMetadata.totalElements);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('applications-error', message, `${applicationLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [currentPage, itemsPerPage, searchText, accessLevelFilter, myApplications, defaultOnly, showToast, applicationLabels, loggedUser]);

  const resetApplications = useCallback(() => {
    setApplications(undefined);
    getApplications();
  }, [getApplications]);

  useEffect(() => {
    resetApplications();
    // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    if (currentPage > 1) setCurrentPage(1);
    else resetApplications();
    // eslint-disable-next-line
  }, [myApplications, defaultOnly, accessLevelFilter, searchText, itemsPerPage]);

  const handleConfirm = useCallback(() => {
    onConfirm?.(editingApplications);
  }, [onConfirm, editingApplications]);

  const toggleSelection = useCallback((application: BdaApplication) => () => {
    setEditingApplications(selected => {
      return selected.map(app => app.id).includes(application.id)
        ? selected.filter(app => app.id !== application.id)
        : selected.concat(application)
    });
  }, []);

  const handlePageChange = useCallback((page: number, pageSize: number) => {
    setCurrentPage(page)
    setItemsPerPage(pageSize);
  }, []);

  const handleFilterChange = useCallback((field: string) => (evt: any) => {
    switch (field) {
      case 'search':
        setSearchText(evt);
        break;

      case 'accessLevel':
        setAccessLevelFilter(evt);
        break;

      case 'owned':
        setMyApplications(evt.target.checked);
        break;

      case 'default':
        setDefaultOnly(evt.target.checked);
        break;

      case 'selected':
        setShowSelected(evt.target.checked);
        break;
    }
  }, []);

  const filtederApplications = useMemo(() => {
    return showSelected ? editingApplications : applications;
  }, [applications, editingApplications, showSelected]);
  const selectedApplicationIds = useMemo(() => editingApplications.map(app => app.id!), [editingApplications]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      afterClose={onCancel}
      title={`Choose Specific ${applicationLabels.plural}`}
      style={{ top: 50 }}
      width={1000}
      footer={
        <div className="d-flex justify-content-between">
          <Button size="sm" type="button" variant="light" className="border" onClick={onCancel}>Cancel</Button>
          <Button size="sm" type="button" disabled={!editingApplications.length} variant="success" onClick={handleConfirm}>Confirm</Button>
        </div>
      }
    >
      <br />

      <Row className="align-items-center mb-3">
        <Col>
          <AppSearch value={searchText} onChange={handleFilterChange('search')} />
        </Col>
        <Col className="px-2">
          <Select
            placeholder="Select privacy level"
            value={accessLevelFilter}
            allowClear
            onChange={handleFilterChange('accessLevel')}
            className="w-100"
          >
            {accessLevelOptions
              .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
              .map((option, index) => (
                <Select.Option key={index} value={option.value} label={option.label}>
                  <div>
                    <FontAwesomeIcon icon={option.icon} fixedWidth />
                    <span className="ms-2">{option.label}</span>
                  </div>
                </Select.Option>
              ))}
          </Select>
        </Col>
        <Col className="ps-2">
          <Checkbox checked={myApplications} onChange={handleFilterChange('owned')}>My {applicationLabels.plural}</Checkbox>
          <Checkbox className="ms-3" checked={defaultOnly} onChange={handleFilterChange('default')}>Default Only</Checkbox>
        </Col>
      </Row>

      {editingApplications.length > 0 && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={
            <div className="d-flex justify-content-between align-items-center">
              <Col>
                You selected <b>{editingApplications.length} {editingApplications.length > 1 ? applicationLabels.plural : applicationLabels.singular}</b> so far.
              </Col>
              <Checkbox checked={showSelected} onChange={handleFilterChange('selected')}>Show selected</Checkbox>
            </div>
          }
        />
      )}

      {!applications && <AppLoader className="py-5 my-5" justify="center" size={80} />}

      {filtederApplications && (
        <Row className="gx-2 applications-wrapper">
          {filtederApplications?.map(app => (
            <Col key={app.id} md={6} lg={4} className="mb-2">
              <ApplicationItem
                application={app}
                selected={selectedApplicationIds.includes(app.id!)}
                onSelect={toggleSelection(app)}
              />
            </Col>
          ))}
        </Row>
      )}

      {!showSelected && (
        <Pagination
          className="text-center"
          current={currentPage}
          pageSize={itemsPerPage}
          total={totalApplications}
          onChange={handlePageChange}
          pageSizeOptions={[12, 24, 48, 60]}
        />
      )}
    </Modal>
  )
})

/* ApplicationItem.tsx */
type ApplicationItemProps = {
  application: BdaApplication;
  selected?: boolean;
  onSelect?: () => void;
  onRemove?: () => void;
  selectable?: boolean;
  removable?: boolean;
}

const ApplicationItem = memo(({ application, selectable = true, removable, selected, onSelect, onRemove }: ApplicationItemProps) => {
  const handleSelect = useCallback(() => onSelect?.(), [onSelect]);
  const handleRemove = useCallback(() => onRemove?.(), [onRemove]);

  return (
    <div className={classNames('application-selectable-item', { selected })} key={application.id}>
      {selectable && (
        <div className="select-icon ps-3">
          <FontAwesomeIcon size="lg" className="action" icon={selected ? solid('check-square') : regular('square')} onClick={handleSelect} />
        </div>
      )}
      {removable && (
        <IconButton type="button" tooltip="Remove" className="text-danger ps-3">
          <FontAwesomeIcon size="lg" icon={solid('times')} onClick={handleRemove} />
        </IconButton>
      )}
      <Col className="px-3 overflow-hidden">
        <div className="text-truncate">
          <Link to={`/bda-applications/${application.id}`} target="_blank">{application.name}</Link>
        </div>
        <Tooltip title="Access Level">
          <Badge>{application.accessLevel}</Badge>
        </Tooltip>
        <Tooltip title="Version">
          <Badge bg="light" className="ms-2 border text-dark">
            <FontAwesomeIcon icon={solid('tag')} />
            <span className="ms-2">{application.version}</span>
          </Badge>
        </Tooltip>
        <Tooltip title="Created">
          <small className="ms-2">{formatDate(application.createDateTime!)}</small>
        </Tooltip>
        <UserItem className="sm p-1" user={application.owner} organization={application.organization} />
      </Col>
    </div>
  )
});
